// Shop events for ads and analytics: the Meta Pixel (Facebook/Instagram ads)
// and Google Analytics 4 (through Firebase, started in AppRoot).
// The pixel id comes from the server (META_PIXEL_ID there), so nothing loads
// until it is set. Staff aren't tracked, so the ad audiences are customers.
// A purchase also goes to Meta from the server (backend/services/metaCapi.js)
// with the same event id (the order number), and Meta counts it once.
import axios from "axios";
import { getApp } from "firebase/app";
import { getAnalytics, logEvent } from "firebase/analytics";
import { API_BASE_URL } from "common/apiUrls";
import { getLoggedInUserLevel, isCustomer } from "utils/auth";

let pixelReady = null; // a promise: true once the pixel is loaded

const isStaff = () => !!getLoggedInUserLevel() && !isCustomer();

function loadPixel(id) {
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js",
  );
  /* eslint-enable */
  window.fbq("init", id);
}

function pixel() {
  if (!pixelReady) {
    pixelReady = axios
      .get(`${API_BASE_URL}/shop/tracking`)
      .then((res) => {
        const id = res.data && res.data.result && res.data.result.metaPixelId;
        if (!id) return false;
        loadPixel(id);
        return true;
      })
      .catch(() => false);
  }
  return pixelReady;
}

function meta(event, params, eventId) {
  if (isStaff()) return;
  pixel().then((ok) => {
    if (!ok) return;
    if (eventId) window.fbq("track", event, params, { eventID: eventId });
    else window.fbq("track", event, params);
  });
}

function google(event, params) {
  if (isStaff()) return;
  try {
    logEvent(getAnalytics(getApp()), event, params);
  } catch (e) {
    // analytics blocked or not started: the shop works without it
  }
}

// Every page the customer opens (Google counts pages on its own).
export function trackPage() {
  meta("PageView");
}

// line: { itemId, name?, price? (per cake), quantity }
export function trackAddToCart({ itemId, name, price, quantity = 1 }) {
  const value = price ? price * quantity : undefined;
  meta("AddToCart", {
    content_ids: [itemId],
    content_type: "product",
    contents: [{ id: itemId, quantity }],
    ...(value ? { value, currency: "INR" } : {}),
  });
  google("add_to_cart", {
    currency: "INR",
    value,
    items: [{ item_id: itemId, item_name: name || itemId, quantity }],
  });
}

const itemsOf = (lines) =>
  lines.map((l) => ({ id: l.itemId, quantity: l.quantity || 1 }));

// lines: [{ itemId, quantity }], value: rupees
export function trackCheckout(lines, value) {
  meta("InitiateCheckout", {
    content_ids: lines.map((l) => l.itemId),
    contents: itemsOf(lines),
    num_items: lines.reduce((n, l) => n + (l.quantity || 1), 0),
    value,
    currency: "INR",
  });
  google("begin_checkout", {
    currency: "INR",
    value,
    items: lines.map((l) => ({ item_id: l.itemId, quantity: l.quantity || 1 })),
  });
}

// order: the website order the server placed (paid online)
export function trackPurchase(order) {
  const value = order.paid || order.subtotal;
  const items = order.items || [];
  meta(
    "Purchase",
    {
      content_ids: items.map((i) => i.itemId),
      contents: items.map((i) => ({ id: i.itemId, quantity: i.quantity })),
      num_items: items.reduce((n, i) => n + (i.quantity || 1), 0),
      value,
      currency: "INR",
    },
    order.orderNo,
  );
  google("purchase", {
    transaction_id: order.orderNo,
    currency: "INR",
    value,
    shipping: order.deliveryCharge || 0,
    items: items.map((i) => ({
      item_id: i.itemId,
      item_name: i.name,
      price: i.total && i.quantity ? i.total / i.quantity : undefined,
      quantity: i.quantity,
    })),
  });
}

// Meta's browser ids, sent with the order so the server's purchase event
// matches this visitor (backend/services/metaCapi.js).
export function metaIds() {
  const cookie = (name) => {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : undefined;
  };
  return { fbp: cookie("_fbp"), fbc: cookie("_fbc") };
}
